import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { ColorsView, ModelInformationView } from "@slfinrtl/ui-bff-routes"
import { getColorsRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { useModal } from "src/context/modal/useModal"
import { useBff } from "src/hooks"
import type { SelectExteriorColorModalProps } from "src/modals/colors/selectExteriorColorModal/SelectExteriorColorModal"
import { SelectExteriorColorModal } from "src/modals/colors/selectExteriorColorModal/SelectExteriorColorModal"
import type { SelectInteriorModalProps } from "src/modals/colors/selectInteriorModal/SelectInteriorModal"
import { SelectInteriorModal } from "src/modals/colors/selectInteriorModal/SelectInteriorModal"
import type { SelectRoofColorModalProps } from "src/modals/colors/selectRoofColorModal/SelectRoofColorModal"
import { SelectRoofColorModal } from "src/modals/colors/selectRoofColorModal/SelectRoofColorModal"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"

export interface ColorsProps {
	exteriorColor?: {
		hexCode?: string
		name?: string
		showExteriorColorModal: () => void
	}
	interior?: {
		color?: {
			cockpitColorHexCode: string
			cockpitStitchingColorHexCode: string
			seatColorHexCode: string
			seatStitchingColorHexCode: string
		}
		name?: string
		showInteriorModal: () => void
	}
	roofColor?: {
		hexCode?: string
		name?: string
		showRoofColorModal: () => void
	}
}

export const useColors = (): ColorsProps | null => {
	const [colorsView, setColorsView] = useState<ColorsView | null>()
	const [modelInformationView, setModelInformationView] = useState<ModelInformationView | null>()

	const { inventoryId, subscribe } = useVehicleDetailsContext()
	const { locale } = useDealerContext()

	const showSelectExteriorColorModal = useModal<SelectExteriorColorModalProps>(SelectExteriorColorModal)
	const showSelectRoofColorModal = useModal<SelectRoofColorModalProps>(SelectRoofColorModal)
	const showSelectInteriorModal = useModal<SelectInteriorModalProps>(SelectInteriorModal)

	useEffect(() => subscribe("modelInformationChanged", setModelInformationView), [subscribe])

	const fetchColors = useBff(
		getColorsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		},
		"inline",
	)

	useEffect(() => {
		fetchColors()
			.then(setColorsView)
			.catch(() => setColorsView(null))
	}, [fetchColors, locale])

	const handleShowSelectExteriorColorModalClicked = () => {
		return (
			!!modelInformationView &&
			showSelectExteriorColorModal({
				exteriorColor: colorsView?.exteriorColor,
				inventoryId,
				orderType: {
					code: modelInformationView.orderTypeCode,
					modelYear: modelInformationView.modelYear,
				},
				setExteriorColor: (exteriorColor) => setColorsView((existing) => ({ ...existing!, exteriorColor })),
			})
		)
	}

	const handleShowSelectRoofColorModalClicked = () =>
		!!modelInformationView &&
		showSelectRoofColorModal({
			inventoryId,
			orderType: {
				code: modelInformationView.orderTypeCode,
				modelYear: modelInformationView.modelYear,
			},
			roofColor: colorsView?.roofColor,
			setRoofColor: (roofColor) => setColorsView((existing) => ({ ...existing!, roofColor })),
		})

	const handleShowSelectInteriorModalClicked = () =>
		!!modelInformationView &&
		showSelectInteriorModal({
			interior: colorsView?.interior,
			inventoryId,
			orderType: {
				code: modelInformationView.orderTypeCode,
				modelYear: modelInformationView.modelYear,
			},
			setInterior: (interior) => setColorsView((existing) => ({ ...existing!, interior })),
		})

	return colorsView === null
		? null
		: {
				exteriorColor: {
					hexCode: colorsView?.exteriorColor?.hexCode,
					name: colorsView?.exteriorColor?.name[locale] ?? emptyPlaceholder,
					showExteriorColorModal: handleShowSelectExteriorColorModalClicked,
				},
				interior: {
					color: colorsView?.interior?.color
						? {
								cockpitColorHexCode: colorsView.interior.color.cockpitColorHexCode,
								cockpitStitchingColorHexCode: colorsView.interior.color.cockpitStitchingColorHexCode,
								seatColorHexCode: colorsView.interior.color.seatColorHexCode,
								seatStitchingColorHexCode: colorsView.interior.color.seatStitchingColorHexCode,
							}
						: undefined,
					name: colorsView?.interior?.name[locale] ?? emptyPlaceholder,
					showInteriorModal: handleShowSelectInteriorModalClicked,
				},
				roofColor: colorsView?.roofColorAvailable
					? {
							hexCode: colorsView?.roofColor?.hexCode,
							name: colorsView?.roofColor?.name[locale] ?? emptyPlaceholder,
							showRoofColorModal: handleShowSelectRoofColorModalClicked,
						}
					: undefined,
			}
}
